import { useEffect, useMemo, useState } from 'react'
import { Bot, Coins, GitBranch, Info, ShieldCheck } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { StatCard } from '@/components/StatCard'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import {
  useAiGatewayLimits,
  useAiGatewayMe,
  useSetAiGatewayLimits,
} from '@/features/team/queries'
import type { AiGatewayRoleLimit } from '@/features/team/types'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

// node-pg returns BIGINT as a string; the API now coerces, but harden the
// formatters anyway so any missed coercion doesn't render "NaN%" again.
function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function formatLimit(v: unknown): string {
  const n = num(v)
  if (n === 0) return 'Unlimited'
  return n.toLocaleString()
}

function formatCount(v: unknown): string {
  return num(v).toLocaleString()
}

function pct(used: unknown, limit: unknown): number {
  const u = num(used)
  const l = num(limit)
  if (l === 0) return 0
  return Math.min(100, Math.round((u / l) * 100))
}

export function AiGatewayTab() {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('team.ai_gateway.manage')
  const { toast } = useToast()
  const limitsQ = useAiGatewayLimits()
  const meQ = useAiGatewayMe()
  const setLimits = useSetAiGatewayLimits()

  const [editing, setEditing] = useState<AiGatewayRoleLimit | null>(null)
  const [tokenLimit, setTokenLimit] = useState('0')
  const [maxAgents, setMaxAgents] = useState('0')
  const [maxWorkflows, setMaxWorkflows] = useState('0')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (editing) {
      setTokenLimit(String(editing.monthly_token_limit))
      setMaxAgents(String(editing.max_agents))
      setMaxWorkflows(String(editing.max_workflows))
      setError(null)
    }
  }, [editing])

  const myLimits = meQ.data?.role
  const myUsage = meQ.data?.usage
  const plan = limitsQ.data?.plan ?? 'free'
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1)
  const periodEnd = myUsage?.period_end
  const daysLeft = useMemo(() => {
    if (!periodEnd) return null
    const ms = new Date(periodEnd).getTime() - Date.now()
    if (Number.isNaN(ms)) return null
    return Math.max(0, Math.ceil(ms / 86_400_000))
  }, [periodEnd])

  const close = () => setEditing(null)

  const submit = async () => {
    if (!editing) return
    const t = Number(tokenLimit)
    const a = Number(maxAgents)
    const w = Number(maxWorkflows)
    if (!Number.isFinite(t) || t < 0 || !Number.isFinite(a) || a < 0 || !Number.isFinite(w) || w < 0) {
      setError('All limits must be non-negative whole numbers (0 = unlimited).')
      return
    }
    try {
      await setLimits.mutateAsync({
        roleId: editing.role_id,
        body: {
          monthlyTokenLimit: Math.floor(t),
          maxAgents: Math.floor(a),
          maxWorkflows: Math.floor(w),
        },
      })
      toast({ title: 'Limits updated.' })
      close()
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const columns: ColumnDef<AiGatewayRoleLimit>[] = [
    {
      accessorKey: 'role_name',
      header: 'Role',
      cell: ({ row }) => (
        <span className="font-semibold">
          {row.original.role_name}
          {row.original.is_system ? (
            <Badge variant="secondary" className="ml-2">
              system
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      accessorKey: 'monthly_token_limit',
      header: 'Monthly tokens',
      cell: ({ row }) => formatLimit(row.original.monthly_token_limit),
    },
    {
      accessorKey: 'max_agents',
      header: 'Max AI agents',
      cell: ({ row }) => formatLimit(row.original.max_agents),
    },
    {
      accessorKey: 'max_workflows',
      header: 'Max workflows',
      cell: ({ row }) => formatLimit(row.original.max_workflows),
    },
    {
      accessorKey: 'member_count',
      header: 'Members',
      cell: ({ row }) => row.original.member_count,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        canManage ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(row.original)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  if (limitsQ.isLoading || meQ.isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  const rows = limitsQ.data?.roles ?? []

  return (
    <div className="space-y-4">
      <Alert>
        <Info className="size-4" aria-hidden />
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>
            Workspace plan{' '}
            <Badge variant="brand" className="ml-0.5 align-middle">
              {planLabel}
            </Badge>
            . Limits scale with the plan - upgrading on{' '}
            <a className="underline" href="/subscriptions">
              Subscriptions
            </a>{' '}
            automatically lifts every role still on its current default.
          </span>
          <span className="text-muted-foreground">
            Set per-role limits for monthly AI tokens, AI agents, and workflows. Use{' '}
            <code className="rounded bg-muted px-1">0</code> for unlimited.
          </span>
        </AlertDescription>
      </Alert>

      {myLimits ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <StatCard
            label={`AI tokens this period (${myLimits.role_name})`}
            value={`${formatCount(myUsage?.tokens_used)} / ${formatLimit(myLimits.monthly_token_limit)}`}
            hint={(() => {
              const parts: string[] = []
              if (daysLeft != null) parts.push(`${daysLeft} day${daysLeft === 1 ? '' : 's'} left in period`)
              if (num(myLimits.monthly_token_limit) > 0) {
                parts.push(`${pct(myUsage?.tokens_used, myLimits.monthly_token_limit)}% used`)
              }
              return parts.join(' · ') || undefined
            })()}
            icon={Coins}
          />
          <StatCard
            label="AI agents created"
            value={`${formatCount(myUsage?.agents_count)} / ${formatLimit(myLimits.max_agents)}`}
            hint={
              num(myLimits.max_agents) > 0
                ? `${pct(myUsage?.agents_count, myLimits.max_agents)}% used`
                : undefined
            }
            icon={Bot}
          />
          <StatCard
            label="Workflows created"
            value={`${formatCount(myUsage?.workflows_count)} / ${formatLimit(myLimits.max_workflows)}`}
            hint={
              num(myLimits.max_workflows) > 0
                ? `${pct(myUsage?.workflows_count, myLimits.max_workflows)}% used`
                : undefined
            }
            icon={GitBranch}
          />
        </div>
      ) : null}

      <Card>
        <CardContent className="p-0">
          <DataTable columns={columns} rows={rows} />
        </CardContent>
      </Card>

      <Modal
        open={!!editing}
        onOpenChange={(v) => !v && close()}
        title={editing ? `AI Gateway limits: ${editing.role_name}` : 'AI Gateway limits'}
        description="0 means unlimited. Limits are per active member of the role."
        className="sm:max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={setLimits.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={setLimits.isPending}>
              <ShieldCheck className="size-4" aria-hidden />
              Save limits
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Monthly AI tokens per member"
            type="number"
            min={0}
            step={1000}
            value={tokenLimit}
            onChange={(e) => setTokenLimit(e.currentTarget.value)}
            hint="Resets automatically each billing period."
          />
          <FormField
            label="Max AI agents per member"
            type="number"
            min={0}
            step={1}
            value={maxAgents}
            onChange={(e) => setMaxAgents(e.currentTarget.value)}
          />
          <FormField
            label="Max workflows per member"
            type="number"
            min={0}
            step={1}
            value={maxWorkflows}
            onChange={(e) => setMaxWorkflows(e.currentTarget.value)}
          />
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}
